import {PlanItemList} from "../types/plan";
import {Box, Button, Grid, Modal, TextField, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {Company} from "../types/company";
import {DataGrid} from "@mui/x-data-grid";
import NoRowsOverlay from "./NoRowsOverlay";
import {CheckboxColumn, Column, columnStyle, DateColumn, DateTimeColumn, GrossColumn} from "./Columns";
import {AuctionType, PlanItemState, ProductType} from "../utils/enums";
import {planService} from "../api/services/plan.service";
import {useSnack} from "../utils/useSnack";
import {LoadingButton} from "@mui/lab";

export interface PlanItemModalProps {
    isOpen: boolean
    onClose: () => void
    pickPlanItems?: (plans: PlanItemList[]) => void;
    company: Company;
    financialYear: number,
    auctionType: AuctionType,
    planItemStates: PlanItemState[],
    productType: ProductType,
}


export const PlanItemModal = ({
                                  isOpen, onClose, pickPlanItems, company,
                                  financialYear, auctionType, planItemStates, productType
                              }: PlanItemModalProps) => {

    const modalStyle = {
        position: "absolute" as "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 850, // Указываем нужную ширину
        bgcolor: "background.paper",
        p: 4,
    };

    const {t} = useTranslation();
    const {snack} = useSnack();
    const [planItems, setPlanItems] = React.useState<PlanItemList[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalElements, setTotalElements] = React.useState(0);
    const [searchText, setSearchText] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [selectedRows, setSelectedRows] = React.useState<PlanItemList[]>([]);

    const onSearch = async () => {
        getPlanItemsByCompany();
    }

    const getPlanItemsByCompany = async () => {
        setSelectedRows([])
        setLoading(true);
        try {
            const response = await planService.getPlanItemsWithoutPlan(company!.id, searchText, page, pageSize,
                financialYear, auctionType, planItemStates, productType)
            if (response.status === "success" && response.data) {
                setPlanItems(response.data.data);
                setTotalElements(response.data.totalElements);
            }
        } catch (error) {
            snack(t("snack.error.default"), "error");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getPlanItemsByCompany()
    }, [page, pageSize, isOpen])

    // Обработчик для изменения состояния выбора
    const handleCheckboxChange = (row: PlanItemList, checked: boolean) => {
        if (checked) {
            setSelectedRows((prev) => [...prev, row]);
        } else {
            setSelectedRows((prev) => prev.filter((item) => item.id !== row.id));
        }
    };

    // Кастомный Toolbar, отображающий выбранные строки
    const CustomToolbar = () => {
        return (
            <Box sx={{padding: "4px"}}>
                {selectedRows.length > 0 && (
                    <Typography variant="body2">
                        {selectedRows.length === 1
                            ? "1 строка выбрана"
                            : `${selectedRows.length} строки выбраны`}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box sx={modalStyle}>
                {/* header */}
                <Grid container spacing={2} justifyContent="space-between" sx={{mb: 2}}>
                    <Grid item>
                        <Typography component="h5" variant="h5" sx={{fontSize: {xs: 14, sm: 20}}}>
                            {t("auction.modals.plan_items.title")}
                        </Typography>
                    </Grid>
                    <Grid item xs={2.2} sm={1.2}>
                        <Button onClick={onClose} sx={{
                            color: "black"
                        }}>
                            <CloseIcon/>
                        </Button>
                    </Grid>
                </Grid>
                {/* / header */}

                <Box>
                    {/* search */}
                    <Grid container spacing={2} xs={12} sx={{mb: 2}}>
                        <Grid item xs={9}>
                            <TextField
                                fullWidth color="primary" type="text" size="small"
                                name="searchText" label={t("auction.modals.product.search_by_name")}
                                value={searchText} onChange={e => setSearchText(e.target.value)}
                                onKeyPress={e => {
                                    if (e.key === "Enter") {
                                        onSearch()
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <LoadingButton fullWidth color="primary" variant="contained"
                                           onClick={onSearch} loading={isLoading}
                            >
                                {t("auction.modals.product.search")}
                            </LoadingButton>
                        </Grid>
                    </Grid>
                    {/* / search */}
                    <DataGrid
                        components={{
                            NoRowsOverlay: () => <NoRowsOverlay/>,
                        }}

                        sx={{
                            height: 500,
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "pre-wrap"
                            },
                        }}
                        columnBuffer={4}
                        rows={planItems.length
                            ? planItems
                            : []
                        }
                        columns={[
                            CheckboxColumn<PlanItemList>("action", 50, "cabinet.plan", (value: PlanItemList, checked: boolean) => {
                                handleCheckboxChange(value, checked);
                            }),
                            Column("id", 50, columnStyle, undefined, "cabinet.plan"),
                            Column("productName", 350, columnStyle, undefined, "cabinet.plan"),
                            Column("productType", 100, columnStyle, "product_type", "cabinet.plan"),
                            DateTimeColumn("createdAt", 200, columnStyle, "cabinet.plan"),
                            Column("auctionType", 100, columnStyle, 'auction_type', "cabinet.plan"),
                            Column("amount", 100, columnStyle, undefined, "cabinet.plan"),
                            GrossColumn(150, columnStyle, "cabinet.plan"),
                            Column("state", 200, columnStyle, "plan_state", "cabinet.plan")
                        ]}
                        loading={loading}
                        density="comfortable" style={{whiteSpace: "pre-wrap"}}
                    />
                    {/* Кнопки "Закрыть" и "Применить" */}
                    <Box mt={2} display="flex" justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={onClose}
                            sx={{mr: 2}}
                        >
                            {t("common.close", "Закрыть")}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                if (pickPlanItems) {
                                    pickPlanItems(selectedRows);
                                }
                            }}
                        >
                            {t("common.apply", "Применить")}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}